import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import {
  getMembershipFormConfig,
  getListingConfig,
  selectEventNameFromListingConfig,
  selectIsBannerLayout, selectIsPosterLayout
} from '@checkout-b/checkout/selectors/ui';
import { getXssFilter } from '@showclix-shared-util/xssFilter';
import Button from '@checkout-s/Button';
import MessageInlineBanner from "@showclix-shared/MessageInlineBanner";
import { useTranslation } from '@checkout-c/Translations';
import { fetchReservation } from '@checkout/actions/reservation';
import { wrappedFetch } from '@checkout-h/fetch';
import { setSkipMembership,setHasMembershipContext, membershipViewPage, membershipSuccessSignIn, setCheckoutOverlay } from '@checkout-b/checkout/actions/ui';
import {getEventPriceLevels} from 'checkout/actions/event';
import { getEventSeriesChildListing } from '../../checkout/views/series/actions/event-series';
import { hasHookSubscription } from '@checkout-b/checkout/selectors/hooks';
import { useNavigate } from 'react-router';
import { getListingItem, setListingItem } from "@checkout-b/listing/helpers/storage";
import { Translation } from "@showclix-shared/CheckoutProcess/components/Translation";
import disneyDriver from "./memberships/drivers/disney";

const xssFilter = getXssFilter();

export const MemberLogin = ({ onLogin = () => {}, isOverlay = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const eventName = useSelector(selectEventNameFromListingConfig);
  const formConfig = useSelector(getMembershipFormConfig);
  const listingConfig = useSelector(getListingConfig);
  const { name: sellerName } = useSelector((state) => state.contactInfo);
  const [ formValues, setFormValues ] = useState({});
  const [ membershipError, setMembershipError ] = useState(null);
  const [ isLoading, setIsLoading ] = useState(true);
  const [ isVerificationLinkSent, setVerificationLinkSent ] = useState(false);
  const { t } = useTranslation();
  const apiUrl = useSelector(state => state.ui?.apiUrl);
  const listingSlug = useSelector(state => state.ui?.listingSlug);
  const token = useSelector(state => state.reservation?.token);
  const secret = useSelector(state => state.reservation?.secret);
  const isRecurring = useSelector(state => state.ui.isRecurring);
  const selectedChildEvent = useSelector(state => state.event?.childEvent?.event);
  const hasMembershipViewPageSubscription = useSelector(state => hasHookSubscription(state, 'ADD_MEMBERSHIP_VIEW_HOOK'));
  const hasReportedMembershipView = useRef(false);
  const isBannerLayout = useSelector(selectIsBannerLayout);
  const isPosterLayout = useSelector(selectIsPosterLayout);
  const isBannerOrPoster = isBannerLayout || isPosterLayout;

  const {
    membership_required,
    title_text,
    subtitle_text,
    additional_text,
    auth_fields = {},
    member_button,
    nonmember_button,
    membership_type
  } = formConfig || {};

  const title = title_text || (membership_required ? t('checkout__for_members_only') : t('checkout__membership_login'));
  const subtitle = subtitle_text || t('checkout__membership_subtitle', { sellerName});

  const additionalText = xssFilter.process(additional_text || '');

  // Use external driver for other memberships
  const membershipExternalDriver = membership_type === 'DISNEY_ONEID' ? disneyDriver : null;

  useEffect(() => {
    if (!hasReportedMembershipView.current && hasMembershipViewPageSubscription) {
      dispatch(membershipViewPage());
      hasReportedMembershipView.current = true;
    }
  }, [hasMembershipViewPageSubscription]);

  useEffect(() => {
    setIsLoading(true);
    if (!membershipExternalDriver) {
      setIsLoading(false);
      return;
    }
    membershipExternalDriver.init?.(listingConfig, formConfig, onMembershipDriverInitialized, submitMembershipData, dispatch)
      .then(() => {
        setIsLoading(false);
      });
  }, [membershipExternalDriver])

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    // Add only the fields from auth_fields
    if (formConfig.auth_fields[name]) {
      setMembershipError(null);
      setFormValues((prev) => ({...prev, [name]: value}));
    }
  }

  const onMembershipDriverInitialized = () => {
    setIsLoading(false);
  }

  const memberLoginFormSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    // If we have an external driver
    if (membershipExternalDriver) {
      membershipExternalDriver.submit()
        .then((result) => {
          setIsLoading(result.isLoading);
        })
        .catch((err) => {
          setMembershipError(err.message);
          setIsLoading(false);
        });
      return;
    }
    // Submit if we have a default driver
    submitMembershipData({
      listingId: listingConfig.listingId,
      membership: {
        submit_member: 'submit_member',
        ...formValues
      },
    });
  }

  const submitMembershipData = (membershipData) => {
    setIsLoading(true);
    const url = apiUrl + '/memberships/' + (token ?? '');
    const headers = {
        'Content-Type': 'application/json'
    };
    if (secret) {
      headers['reservation-secret'] = secret;
    }
    // Send request
    wrappedFetch(url , {
      method: 'POST',
      mode: 'cors',
      headers: headers,
      body: JSON.stringify(membershipData),
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.verify) {
          setVerificationLinkSent(true);
          return;
        }
        if (data.success && data.token && data.secret) {
          dispatch(setHasMembershipContext(true));
          dispatch(membershipSuccessSignIn());
          dispatch(fetchReservation(data.token, data.secret)).then(() => {
            // Check if we have a reservation in storage and save one if we don't
            if (!getListingItem(listingSlug, 'token') || !getListingItem(listingSlug, 'secret')) {
              setListingItem(listingSlug, 'token', data.token);
              setListingItem(listingSlug, 'secret', data.secret);
            }

            // Proceed
            if (isRecurring) {
              if (selectedChildEvent){
                dispatch(getEventSeriesChildListing(selectedChildEvent.event_id));
              }
            } else {
              dispatch(getEventPriceLevels());
            }
            // Transition to ticketPicker if loaded in overlay
            if (isOverlay) {
              if (isBannerOrPoster) {
                dispatch(setCheckoutOverlay(false));
              }
              navigate('/');
            }
            if (onLogin) {
              onLogin();
            }
            setIsLoading(false);
          });

        } else {
          setMembershipError(data.errors?.[0]?.detail ?? data.error ?? 'error');
        }
        setIsLoading(false);
      });
  }

  const onSkipMembership = () => {
    dispatch(setSkipMembership(true));
    dispatch(getEventPriceLevels());
    if (isOverlay) {
      if (isBannerOrPoster) {
        dispatch(setCheckoutOverlay(false));
      }
      navigate('/');
    }
    if (onLogin) {
      onLogin();
    }
  }

  return (
    <div className={clsx('listing__member-login', { 'listing__member-login-overlay': isOverlay })}>
      <div className="listing__member-login__content">
        {!isOverlay && <h1 className="listing__member-login__event-name">{eventName}</h1>}
        {isVerificationLinkSent && <div className="listing__member-login__verification-link-sent">
          <Translation>{'checkout__verification_link_sent'}</Translation>
        </div>}
        {!isVerificationLinkSent && <div className="listing__member-login__form">
          <header className="member-login__form__header">
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </header>
          <div className="member-login__form__main">
            <form
              className="member-login__form__auth"
              onSubmit={memberLoginFormSubmit}
              onChange={handleFormChange}
              aria-labelledby='membership-additional-text'
            >
              {Object.values(auth_fields).map(({id, label}) => (
                <input
                  type="text"
                  key={id}
                  id={id}
                  name={id}
                  placeholder={label}
                  autoComplete="off"
                  className="input__control"
                  required
                  aria-label={label}
                />
              ))}
              { membershipError && <MessageInlineBanner variant={MessageInlineBanner.variant.error}>
                <span dangerouslySetInnerHTML={{ __html: membershipError }}></span>
              </MessageInlineBanner> }
              <Button type="submit" className="button--primary" onClick={() => {}} loading={isLoading}>
                {member_button || (<>{t('checkout__continue')} &rsaquo;&rsaquo;</>)}
              </Button>
              {!membership_required && (
                <Button
                  type="button"
                  className="button--secondary"
                  onClick={onSkipMembership}
                  loading={isLoading}
                >
                  {nonmember_button || (<>{t('checkout__not_a_member')} &rsaquo;&rsaquo;</>)}
                </Button>
              )}
            </form>
            <div
              id="membership-additional-text"
              className="member-login__form__additional-text"
              dangerouslySetInnerHTML={{ __html: additionalText }}
            />
          </div>
        </div>}
      </div>
    </div>
  );
};
